import React, { useEffect, useState, useRef } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Auto-rotating testimonial carousel for the LandingPage.
 * Pulls from GET /api/testimonials (public, published only).
 * Renders nothing if no testimonials are published.
 */
export default function TestimonialsCarousel() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/testimonials`)
      .then((r) => r.json())
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));
  }, []);

  // Auto-rotate every 6 seconds
  useEffect(() => {
    if (items.length <= 1 || paused) return undefined;
    timerRef.current = setInterval(() => {
      setIdx((p) => (p + 1) % items.length);
    }, 6000);
    return () => clearInterval(timerRef.current);
  }, [items.length, paused]);

  if (!items.length) return null;
  const t = items[idx];

  const goTo = (i) => setIdx(((i % items.length) + items.length) % items.length);

  return (
    <div
      className="relative bg-gradient-to-br from-stone-50 to-amber-50/40 border border-amber-300/40 rounded-3xl p-6 md:p-8 shadow-xl"
      data-testid="testimonials-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-1.5 bg-amber-500 text-stone-900 text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
          <Star size={12} fill="currentColor" /> What coaches say
        </div>
      </div>

      <div className="relative max-w-2xl mx-auto" data-testid={`testimonial-slide-${idx}`}>
        <Quote size={40} className="text-amber-400/30 absolute -top-3 -left-2" />

        <p
          className="text-stone-800 text-lg md:text-xl text-center italic leading-relaxed mb-5 px-4"
          data-testid="testimonial-quote"
        >
          &ldquo;{t.quote}&rdquo;
        </p>

        {/* Stars */}
        <div className="flex items-center justify-center gap-0.5 mb-3" aria-label={`${t.rating} stars`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              size={14}
              className={n <= (t.rating || 5) ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}
            />
          ))}
        </div>

        {/* Attribution */}
        <div className="flex items-center justify-center gap-3">
          {t.photo_url ? (
            <img
              src={t.photo_url}
              alt={t.name}
              className="w-10 h-10 rounded-full object-cover border-2 border-amber-400"
              loading="lazy"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-green-700 text-white flex items-center justify-center font-bold text-sm">
              {(t.name || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="text-left">
            <div className="font-bold text-sm text-stone-900" data-testid="testimonial-name">{t.name}</div>
            {t.role && <div className="text-xs text-stone-500">{t.role}</div>}
          </div>
        </div>
      </div>

      {/* Controls */}
      {items.length > 1 && (
        <>
          <button
            onClick={() => goTo(idx - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-stone-700"
            aria-label="Previous testimonial"
            data-testid="testimonial-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => goTo(idx + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-stone-700"
            aria-label="Next testimonial"
            data-testid="testimonial-next"
          >
            <ChevronRight size={18} />
          </button>
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'bg-amber-500 w-6' : 'bg-stone-300 w-1.5 hover:bg-stone-400'}`}
                aria-label={`Show testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
